import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './AnalysisGrid.css';
import api from '../api/axiosConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format, isValid, startOfMonth, subMonths, endOfMonth } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// 숫자 포맷
const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const number = parseFloat(num.toString().replace(/,/g, ''));
  if (isNaN(number)) return 0;
  return number.toLocaleString();
};

// 숫자만 추출 (엑셀용)
const parseNumberForExcel = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const number = parseFloat(num.toString().replace(/,/g, ''));
  return isNaN(number) ? 0 : number;
};

const Total2 = () => {
    const [remarkText, setRemarkText] = useState('');
    const [rows, setRows] = useState([]);
    const [totalData, setTotalData] = useState([]);

    const today = new Date();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [fieldId, setFieldId] = useState('');
    const [fieldOptions, setFieldOptions] = useState([]);

    const [rowsWithSubtotal, setRowsWithSubtotal] = useState([]);

    // ⭐️ 합계 상태 분리
    const [totalRow, setTotalRow] = useState({});
    const [taxInvoiceRow, setTaxInvoiceRow] = useState({});
    const [etcRow, setEtcRow] = useState({});

    // 현장 조회
    useEffect(() => {
        api.get(`/api/fieldList`)
            .then((res) => {
                const uniqueOptions = res.data.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.FIELD_ID === item.FIELD_ID)
                );
                setFieldOptions(uniqueOptions);
            })
            .catch((err) => console.error(err));
    }, []);

    // 총괄정산서 현황 조회
    const searchList = useCallback(() => {

        const start = startDate ? format(startDate, 'yyyy-MM-dd') : '';
        const end = endDate ? format(endDate, 'yyyy-MM-dd') : '';

        setRemarkText('');

        api.get(`/api/analysis/searchList4?startDate=${start}&endDate=${end}&fieldId=${fieldId || ''}`)
            .then((res) => {
                const rawData = res.data || [];
                setRows(rawData);
            })
            .catch((err) => console.error(err));

        api.get(`/api/analysis/searchList5?startDate=${start}&endDate=${end}&fieldId=${fieldId || ''}`)
            .then((res) => {
                const Data = res.data || [];
                setTotalData(Data);
                setRemarkText(Data[0]?.REMARK || '');
            })
            .catch((err) => console.error(err));

    }, [startDate, endDate, fieldId]);

    useEffect(() => {
        searchList();
    }, [searchList]);

    // =========================
    // 엑셀 내려받기
    // =========================
    const exportToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('총괄정산서');
            const fieldName = fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || '';
            let rowNum = 1;

            // 열 너비 (4열)
            worksheet.columns = [
                { width: 25 },
                { width: 20 },
                { width: 20 },
                { width: 20 },
            ];

            // ── 행 스타일 헬퍼 ──────────────────────────────────────────
            const styleRow = (rowNum, bgColor = null, bold = false, isData = true) => {
                for (let col = 1; col <= 4; col++) {
                    const cell = worksheet.getCell(rowNum, col);
                    if (bgColor) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                    cell.font = { bold, size: 10, name: '굴림체' };
                    cell.alignment = { horizontal: col === 1 ? 'left' : 'right', vertical: 'middle' };
                    if (isData && col >= 2) cell.numFmt = '#,##0';
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                }
                worksheet.getRow(rowNum).height = 20;
            };

            // 데이터행 추가 헬퍼
            const addDataRow = (label, v1, v2, v3, bgColor = null, bold = false) => {
                const r = worksheet.getRow(rowNum);
                r.getCell(1).value = label;
                r.getCell(2).value = parseNumberForExcel(v1);
                r.getCell(3).value = parseNumberForExcel(v2);
                r.getCell(4).value = parseNumberForExcel(v3);
                styleRow(rowNum, bgColor, bold);
                rowNum++;
            };

            // 1️⃣ 제목
            worksheet.mergeCells(`A${rowNum}:D${rowNum}`);
            const titleCell = worksheet.getCell(`A${rowNum}`);
            titleCell.value = '총 괄 정 산 서';
            titleCell.font = { bold: true, size: 20, name: '굴림체' };
            titleCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(rowNum).height = 40;
            rowNum++;

            // 2️⃣ 공사명 / 공사기간 / 현장대리인
            if (rows.length > 0) {
                const infoLines = [
                    { text: `▣공사명: ${fieldName}`, color: 'FF0040FF', bold: true },
                    { text: `공사기간: ${rows[0]?.START_DATE ?? ''} ~ ${rows[0]?.END_DATE ?? ''}`, color: 'FF000000', bold: false },
                    { text: `현장대리인: ${rows[0]?.MANAGER ?? ''}`, color: 'FF000000', bold: false },
                ];
                for (const info of infoLines) {
                    worksheet.mergeCells(`A${rowNum}:D${rowNum}`);
                    const cell = worksheet.getCell(`A${rowNum}`);
                    cell.value = info.text;
                    cell.font = { bold: info.bold, size: 10, name: '굴림체', color: { argb: info.color } };
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                    worksheet.getRow(rowNum).height = 18;
                    rowNum++;
                }
            }
            rowNum++; // 빈 행

            // 3️⃣ 헤더
            const headerRowNum = rowNum;
            ['구 분', '공 급 가 액', '부 가 세', '합 계'].forEach((h, i) => {
                const cell = worksheet.getCell(headerRowNum, i + 1);
                cell.value = h;
                cell.font = { bold: true, size: 10, name: '굴림체' };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            worksheet.getRow(headerRowNum).height = 22;
            rowNum++;

            // 4️⃣ 계약금액 (연두)
            addDataRow('계약금액',
                totalData[0]?.CON_PROVIDE_AMOUNT,
                totalData[0]?.CON_VAT,
                totalData[0]?.CON_TOTAL_AMOUNT,
                'FFA8E6A8', true
            );

            // 5️⃣ 청구금액
            addDataRow('청구금액',
                totalData[0]?.OUT_PROVIDE_AMOUNT,
                totalData[0]?.OUT_VAT,
                totalData[0]?.OUT_TOTAL_AMOUNT
            );

            // 6️⃣ 미청구금 (하늘)
            addDataRow('미청구금',
                totalData[0]?.UNPAID_PROVIDE_AMOUNT,
                totalData[0]?.UNPAID_VAT,
                totalData[0]?.UNPAID_TOTAL_AMOUNT,
                'FFB6E9FF'
            );

            // 7️⃣ 동적 rows (최소 20행 보장)
            let dataCount = 0;
            for (const row of rows) {
                const bg = row.isEtctotal || row.isSubtotal ? 'FFFFF0AA' : null;
                addDataRow(
                    row.CUSTOMER_NAME || '',
                    row.PROVIDE_AMOUNT,
                    row.VAT,
                    row.TOTAL_AMOUNT,
                    bg,
                    row.isSubtotal || row.isEtctotal
                );
                dataCount++;
            }
            // 빈 행 채우기
            const emptyCount = Math.max(0, 20 - dataCount);
            for (let e = 0; e < emptyCount; e++) {
                const r = worksheet.getRow(rowNum);
                for (let col = 1; col <= 4; col++) {
                    r.getCell(col).value = '';
                    r.getCell(col).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                }
                r.height = 20;
                rowNum++;
            }

            // 8️⃣ 매입합계 (하늘)
            addDataRow('매입합계',
                totalData[0]?.IN_PROVIDE_AMOUNT,
                totalData[0]?.IN_VAT,
                totalData[0]?.IN_TOTAL_AMOUNT,
                'FFB6E9FF', true
            );

            // 9️⃣ 매출-매입 차 (연보라)
            addDataRow('매출-매입 차',
                (totalData[0]?.OUT_PROVIDE_AMOUNT || 0) - (totalData[0]?.IN_PROVIDE_AMOUNT || 0),
                (totalData[0]?.OUT_VAT || 0) - (totalData[0]?.IN_VAT || 0),
                (totalData[0]?.OUT_TOTAL_AMOUNT || 0) - (totalData[0]?.IN_TOTAL_AMOUNT || 0),
                'FFF1E3FF', true
            );

            // 🔟 비고 (B~D 병합)
            const remarkRow = worksheet.getRow(rowNum);
            remarkRow.getCell(1).value = '비고';
            worksheet.mergeCells(`B${rowNum}:D${rowNum}`);
            remarkRow.getCell(2).value = remarkText ? remarkText : '';
            for (let col = 1; col <= 4; col++) {
                const cell = remarkRow.getCell(col);
                cell.font = { size: 10, name: '굴림체' };
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
            remarkRow.height = 20;
            const tableEndRow = rowNum;
            rowNum++;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `총괄정산서_${fieldName}_${format(new Date(), 'yyyyMMdd')}.xlsx`);

        } catch (error) {
            console.error('엑셀 내보내기 오류:', error);
            alert('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };

    // 인쇄
    const handlePrint = () => {
        const printContents = document.getElementById('print-area').innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload();
    };

  //저장
  const handleSave = useCallback(() => {
      if (!fieldId) return alert('현장을 선택해주세요.');
      if (!window.confirm('저장하시겠습니까?')) return;

      let sendData = {fieldId: fieldId, remark: remarkText}

      api.post(`/api/field/saveRemark`, sendData)
          .then(() => alert('저장되었습니다.'))
          .catch((err) => alert('저장 중 오류가 발생했습니다.\n' + err));
  }, [fieldId, remarkText]);

    // =========================
    // 렌더링
    // =========================
    return (
        <div className="grid-container">
            <h2>총괄정산서</h2>

            <div className="gh-band">
                <div className="gh-band__row">
                    <div className="gh-band__title">
                        <span className="gh-octo" aria-hidden>▶</span>
                        <span>조회조건</span>
                    </div>
                </div>
                <div className="gh-band__filters">

                    <div className="filter-item gh-field">
                        <label>현장</label>
                        <Autocomplete
                            options={fieldOptions}
                            value={fieldOptions.find((option) => option.FIELD_ID === fieldId) || null}
                            onChange={(event, newValue) => {
                                setFieldId(newValue ? newValue.FIELD_ID : '');
                            }}
                            getOptionLabel={(option) => option?.FIELD_NAME || ''}
                            isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="현장 선택"
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    sx={{ px: 1, minWidth: 200 }}
                                />
                            )}
                        />
                    </div>

                    <div className="filter-item gh-field">
                      <label>발행일자</label>
                      <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center', // 내부 요소 중앙 정렬
                          gap: '0', // 갭을 0으로 하고 내부에서 조절
                          border: '1px solid #d0d7de',
                          borderRadius: '4px',
                          padding: '0 4px',
                          height: '32px',
                          backgroundColor: '#fff',
                          width: '230px', // 너비를 약간 줄여서 더 콤팩트하게 설정
                          overflow: 'hidden',
                          flexShrink: 0 // 부모에 의해 찌그러지지 않도록 보호
                        }}>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          dateFormat="yyyy-MM-dd"
                          locale={ko}
                          placeholderText="시작일"
                          wrapperStyle={{ width: '105px', flexShrink: 0 }} // wrapper 너비 고정
                          customInput={
                            <input style={{
                              width: '105px',
                              border: 'none', // 테두리 제거 필수
                              outline: 'none',
                              fontSize: '12px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              background: 'transparent',
                              padding: 0
                            }} />
                          }
                        />

                        <span style={{ color: '#d0d7de', fontSize: '12px', flexShrink: 0 }}>~</span>

                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          dateFormat="yyyy-MM-dd"
                          locale={ko}
                          placeholderText="종료일"
                          wrapperStyle={{ width: '105px', flexShrink: 0 }}
                          customInput={
                            <input style={{
                              width: '105px',
                              border: 'none', // 테두리 제거 필수
                              outline: 'none',
                              fontSize: '12px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              background: 'transparent',
                              padding: 0
                            }} />
                          }
                        />
                      </div>
                    </div>

                    <div className="filter-actions gh-actions">
                        <button className="btn" onClick={searchList}>조회</button>
                    </div>
                </div>
            </div>

            <div className="button-group-right">
                <button onClick={handleSave} className="btn">비고 저장</button>
                <button onClick={exportToExcel} className="btn btn-primary">엑셀 내려받기</button>
                <button onClick={handlePrint} className="btn btn-ghost">출력하기</button>
            </div>

            <div id="print-area">
                <div style={{ border: '1px solid black', textAlign: 'center' }}>
                    <span style={{ fontSize: '20px',fontWeight: 'bold'}}>총 괄 정 산 서</span>
                </div>
                <div style={{ marginBottom: '10px'}}>
                    {rows.length > 0 && (
                        <>
                            <span style={{fontWeight: 'bold', color: '#0040ff'}} >▣공사명: {fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || ''}</span>
                            <br />
                            <span>공사기간: {rows[0]?.START_DATE ?? ''} ~ {rows[0]?.END_DATE ?? ''} </span>
                            <br />
                            <span>현장대리인: {rows[0]?.MANAGER ?? ''}</span>
                        </>
                    )}

                </div>

                <div className="grid-table-wrapper">
                    <table className="grid-table">
                        <colgroup>
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '25%' }} />
                        </colgroup>
                        <thead>
                                <tr>
                                    <th style={{ width: '200px' }} rowSpan="2" colSpan="1"  ><strong>구 분</strong></th>
                                </tr>
                                <tr>
                                   <th>공 급 가 액</th><th>부 가 세</th><th>합 계</th>
                                </tr>
                        </thead>

                        <tbody>

                                {/* 1. 세금계산서 합계 (ETC_YN != 'Y') */}
                                <tr style={{marginTop: '50px'}}>
                                    <td style={{ background: '#f2fff2', textAlign: 'left'}}><span><strong>계약금액</strong></span></td>
                                    <td style={{ background: '#f2fff2' }}><span className="right-align">{formatNumber(totalData[0]?.CON_PROVIDE_AMOUNT||0)}</span></td>
                                    <td style={{ background: '#f2fff2' }}><span className="right-align">{formatNumber(totalData[0]?.CON_VAT||0)}</span></td>
                                    <td style={{ background: '#f2fff2' }}><span className="right-align">{formatNumber(totalData[0]?.CON_TOTAL_AMOUNT||0)}</span></td>
                                </tr>
                                <tr>
                                    <td style={{textAlign: 'left'}}><span>청구금액</span></td>
                                    <td><span className="right-align">{formatNumber(totalData[0]?.OUT_PROVIDE_AMOUNT||0)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalData[0]?.OUT_VAT||0)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalData[0]?.OUT_TOTAL_AMOUNT||0)}</span></td>
                                </tr>
                                <tr>
                                    <td style={{ background: '#d4f1ff', textAlign: 'left'}} ><span>미청구금</span></td>
                                    <td style={{ background: '#d4f1ff' }}><span className="right-align">{formatNumber(totalData[0]?.UNPAID_PROVIDE_AMOUNT||0)}</span></td>
                                    <td style={{ background: '#d4f1ff' }}><span className="right-align">{formatNumber(totalData[0]?.UNPAID_VAT||0)}</span></td>
                                    <td style={{ background: '#d4f1ff' }}><span className="right-align">{formatNumber(totalData[0]?.UNPAID_TOTAL_AMOUNT||0)}</span></td>
                                </tr>

                            {(() => {
                                const result = [];
                                let i = 0;

                                while (i < rows.length) {
                                    const currentRow = rows[i];
                                    const currentMonth = currentRow.ISSUED_MONTH;

                                    let spanCount = 0;
                                    for (let j = i; j < rows.length; j++) {
                                        if (rows[j].ISSUED_MONTH === currentMonth) {
                                            spanCount++;
                                        } else {
                                            break;
                                        }
                                    }

                                    for (let k = 0; k < spanCount; k++) {
                                        const row = rows[i + k];
                                        const isSubtotal = row.isSubtotal;

                                        result.push(
                                            <tr key={i + k} className={row.isEtctotal ? 'etctotal-row' : (isSubtotal ? 'subtotal-row' : '')}>
                                                <td style={{ textAlign: 'left' }}><span>{row.CUSTOMER_NAME}</span></td>
                                                <td><span className="right-align">{formatNumber(row.PROVIDE_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.VAT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.TOTAL_AMOUNT)}</span></td>
                                            </tr>
                                        );
                                    }
                                    i += spanCount;
                                }

                                // 최소 15줄 보장 - 빈 행 채우기
                                const minRows = 20;
                                const emptyCount = minRows - result.length;
                                for (let e = 0; e < emptyCount; e++) {
                                    result.push(
                                        <tr key={`empty-${e}`}>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                        </tr>
                                    );
                                }

                                return result;
                            })()}

                            <tr>
                                <td style={{ background: '#d4f1ff', textAlign: 'left'}}><span><strong>매입합계</strong></span></td>
                                <td style={{ background: '#d4f1ff'}}><strong><span className="right-align">{formatNumber(totalData[0]?.IN_PROVIDE_AMOUNT||0)}</span></strong></td>
                                <td style={{ background: '#d4f1ff'}}><strong><span className="right-align">{formatNumber(totalData[0]?.IN_VAT||0)}</span></strong></td>
                                <td style={{ background: '#d4f1ff'}}><strong><span className="right-align">{formatNumber(totalData[0]?.IN_TOTAL_AMOUNT||0)}</span></strong></td>
                            </tr>
                            <tr>
                                <td style={{background: '#f1e3ff', textAlign: 'left'}}><span><strong>매출-매입 차</strong></span></td>
                                <td style={{background: '#f1e3ff'}}><strong><span className="right-align">{formatNumber((totalData[0]?.OUT_PROVIDE_AMOUNT - totalData[0]?.IN_PROVIDE_AMOUNT) || 0)}</span></strong></td>
                                <td style={{background: '#f1e3ff'}}><strong><span className="right-align">{formatNumber((totalData[0]?.OUT_VAT - totalData[0]?.IN_VAT) || 0)}</span></strong></td>
                                <td style={{background: '#f1e3ff'}}><strong><span className="right-align">{formatNumber((totalData[0]?.OUT_TOTAL_AMOUNT - totalData[0]?.IN_TOTAL_AMOUNT) || 0)}</span></strong></td>
                            </tr>
                            <tr>
                                <td style={{textAlign: 'left'}}><span><strong>비고</strong></span></td>
                                <td style={{textAlign: 'left'}} colSpan="3">
                                    <input
                                        value={remarkText}
                                        onChange={(e) => setRemarkText(e.target.value)}
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            outline: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            cursor: 'text',
                                            textAlign: 'left',
                                        }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    );
};

export default Total2;